import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Stack, Paper, Avatar, IconButton, Grid, CircularProgress,
  FormControlLabel, Switch
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { ProductPreviewModern } from '../../components/ProductPreviewModern';
import { KeywordSelector } from '../../components/KeywordSelector';
import { OptionEditor } from '../../components/OptionEditor';
import { OptionModal } from '../../components/OptionModal'; // Added import for OptionModal
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next'; // Supabase 클라이언트 import
import { useAuth } from '../../components/AuthProvider';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';
import { uploadProductMainImage, uploadProductDetailImage, deleteMultipleFromR2 } from '../../utils/r2Upload';
import { compressProductMainImage, compressProductDetailImage } from '../../utils/imageCompression';

const productImages = [
  'assets/images/product1.png',
  'assets/images/product1_specific.jpg',
  'assets/images/banner.jpg',
];

// 이미지 크기 체크 함수
const checkImageSize = (file: File): Promise<{width: number, height: number}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      reject(new Error('이미지 파일을 읽을 수 없습니다.'));
      URL.revokeObjectURL(img.src);
    };

    img.src = URL.createObjectURL(file);
  });
};

// 이미지 업로드 헬퍼 함수 - R2로 업로드
async function uploadImage(file: File, productId: string, index: number, type: 'main' | 'detail') {
    try {
        console.log(`🚀 R2로 ${type} 이미지 업로드 시작: ${file.name}`);

        let uploadUrl;
        if (type === 'main') {
            uploadUrl = await uploadProductMainImage(file, productId, index);
        } else {
            uploadUrl = await uploadProductDetailImage(file, productId, index);
        }

        if (uploadUrl && typeof uploadUrl === 'string') {
            console.log(`✅ R2 ${type} 이미지 업로드 성공:`, uploadUrl);
            return uploadUrl;
        } else {
            console.error(`❌ R2 ${type} 이미지 업로드 실패:`, uploadUrl);
            return null;
        }
    } catch (error) {
        console.error(`❌ R2 ${type} 이미지 업로드 중 오류:`, error);
        return null;
    }
}

// URL을 File 객체로 변환하는 함수
const urlToFile = async (url: string, filename: string): Promise<File> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  } catch (error) {
    console.error('URL을 File로 변환 실패:', error);
    throw error;
  }
};

export default function AddProducts() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const navigate = useNavigate();
  const location = useLocation();
  const [name, setName] = useState('');
  const [priceValue, setPriceValue] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [images, setImages] = useState<File[]>([]); // 대표 이미지
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [detailImages, setDetailImages] = useState<File[]>([]); // 상세 이미지
  // 상세 이미지 드래그 상태 추가
  const [detailDraggingIdx, setDetailDraggingIdx] = useState<number | null>(null);
  const [detailDragOverIdx, setDetailDragOverIdx] = useState<number | null>(null);
  // 기존 이미지 URL 저장 (수정 모드용)
  const [existingMainImageUrls, setExistingMainImageUrls] = useState<string[]>([]);
  const [existingDetailImageUrls, setExistingDetailImageUrls] = useState<string[]>([]);
  // 원본 이미지 URL (삭제 감지용)
  const [originalMainImageUrls, setOriginalMainImageUrls] = useState<string[]>([]);
  const [originalDetailImageUrls, setOriginalDetailImageUrls] = useState<string[]>([]);
  const [options, setOptions] = useState<any[]>([
    { colorName: '', colorCode: '#000000', sizes: [{ sizeName: '', quantity: 1 }] }
  ]); // 기본 1개 옵션으로 시작
  const [currentImageIdx, setCurrentImageIdx] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const detailFileInputRef = useRef<HTMLInputElement>(null); // 상세 이미지 업로드 ref
  const [optionModalOpen, setOptionModalOpen] = useState(false);
  // 옵션 선택 상태 (색상/사이즈)
  const [selectedOption, setSelectedOption] = useState({ color: '', size: '' });
  
  // 로딩 상태 추가
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);

  // D2C 자사몰 연결 상태
  const [isD2c, setIsD2c] = useState(false);
  const [externalUrl, setExternalUrl] = useState('');
  const [isD2cEnabled, setIsD2cEnabled] = useState(false); // share2dm pro 플랜일 때만 true

  // 상점명 상태 추가
  const [storeName, setStoreName] = useState('My Store');

  // 수정 모드 판단
  const isEditMode = new URLSearchParams(location.search).get('mode') === 'edit';
  const [editingProductId, setEditingProductId] = useState<number | null>(null);

  // 상점명 가져오기
  const fetchStoreName = async () => {
    try {
      // useAuth에서 사용자 정보 가져오기
      if (!user) {
        console.log('사용자 정보 없음');
        return;
      }

      console.log('사용자 ID:', user.id);

      const { data: sellerProfile, error } = await supabase
        .from('seller_profile')
        .select('id, store_name')
        .eq('user_id', user.id)
        .single();

      console.log('seller_profile 조회 결과:', { data: sellerProfile, error });

      if (error) {
        console.error('seller_profile 조회 실패:', error);
        return;
      }

      if (sellerProfile?.store_name) {
        setStoreName(sellerProfile.store_name);
        console.log('상점명 설정됨:', sellerProfile.store_name);
      } else {
        console.log('상점명이 없음, 기본값 사용');
      }

      // share2dm pro 플랜 체크
      if (sellerProfile?.id) {
        const { data: share2dmBrand } = await supabase
          .from('share2dm_brands')
          .select('plan')
          .eq('clozet_seller_id', sellerProfile.id)
          .maybeSingle();
        if (share2dmBrand?.plan === 'pro') {
          setIsD2cEnabled(true);
        }
      }
    } catch (error) {
      console.error('상점명 가져오기 실패:', error);
    }
  };

  // 수정 모드일 때 상품 정보 로드
  const dataLoadedRef = useRef(false);

  useEffect(() => {
    // user가 로드되지 않으면 실행하지 않음
    if (!user) return;

    // 상점명 가져오기
    fetchStoreName();

    const loadEditData = async () => {
      console.log('=== AddProducts useEffect 실행 ===');
      console.log('isEditMode:', isEditMode);
      console.log('location.search:', location.search);
      console.log('이미 데이터 로드됨:', dataLoadedRef.current);
      
      // 이미 데이터를 로드했다면 중복 실행 방지
      if (dataLoadedRef.current) {
        console.log('⚠️ 이미 데이터를 로드했으므로 중복 실행 방지');
        return;
      }
      
      if (isEditMode) {
        console.log('🔍 수정 모드 감지됨');
        setIsLoadingEditData(true);
        const editData = sessionStorage.getItem('editProductData');
        console.log('sessionStorage에서 가져온 데이터:', editData ? '존재함' : '없음');
        
        if (editData) {
          try {
            console.log('📝 JSON 파싱 시작');
            const productData = JSON.parse(editData);
            console.log('파싱된 상품 데이터:', productData);
            
            // 필수 필드 검증
            console.log('🔍 필수 필드 검증 중...');
            console.log('product_id:', productData.product_id);
            console.log('product_name:', productData.product_name);
            
            if (!productData.product_id || !productData.product_name) {
              console.error('❌ 상품 데이터가 불완전합니다:', {
                product_id: productData.product_id,
                product_name: productData.product_name,
                has_id: !!productData.product_id,
                has_name: !!productData.product_name
              });
              alert('상품 데이터가 불완전합니다. 다시 시도해주세요.');
              navigate('/seller/products');
              return;
            }
            
            console.log('✅ 필수 필드 검증 통과');
            
            setEditingProductId(productData.product_id);
            setName(productData.product_name);
            setPriceValue(productData.price.toString());
            setDescription(productData.description || '');
            setKeywords(productData.keywords || []);
            setIsD2c(productData.is_d2c ?? false);
            setExternalUrl(productData.external_url || '');
            
            // 이미지 데이터 설정
            console.log('🖼️ 이미지 데이터 처리 중...');
            console.log('대표 이미지:', productData.main_images);
            console.log('상세 이미지:', productData.detail_images);
            console.log('상세 이미지 타입:', typeof productData.detail_images);
            console.log('상세 이미지 길이:', productData.detail_images?.length);
            console.log('상세 이미지 존재 여부:', !!productData.detail_images);
            
            // 대표 이미지 설정 (URL로 직접 저장)
            if (productData.main_images && productData.main_images.length > 0) {
              console.log('📸 대표 이미지 개수:', productData.main_images.length);
              setExistingMainImageUrls(productData.main_images);
              setOriginalMainImageUrls(productData.main_images); // 원본 URL 저장
              console.log('✅ 대표 이미지 URL 저장 완료:', productData.main_images.length, '개');
            }

            // 상세 이미지 설정 (URL로 직접 저장)
            console.log('🔍 상세 이미지 조건 확인:');
            console.log('- detail_images 존재:', !!productData.detail_images);
            console.log('- detail_images 길이:', productData.detail_images?.length);
            console.log('- 조건 결과:', productData.detail_images && productData.detail_images.length > 0);

            if (productData.detail_images && productData.detail_images.length > 0) {
              console.log('📸 상세 이미지 개수:', productData.detail_images.length);
              setExistingDetailImageUrls(productData.detail_images);
              setOriginalDetailImageUrls(productData.detail_images); // 원본 URL 저장
              console.log('✅ 상세 이미지 URL 저장 완료:', productData.detail_images.length, '개');
            } else {
              console.log('⚠️ 상세 이미지가 없거나 빈 배열입니다.');
              setExistingDetailImageUrls([]);
              setOriginalDetailImageUrls([]);
            }
            
            console.log('📝 폼 필드 설정 완료:', {
              name: productData.product_name,
              price: productData.price,
              description: productData.description || '',
              keywords: productData.keywords || [],
              mainImagesCount: productData.main_images?.length || 0,
              detailImagesCount: productData.detail_images?.length || 0
            });
            
            // 옵션 데이터 설정
            console.log('🔍 옵션 데이터 처리 중...');
            console.log('원본 옵션 데이터:', productData.options);
            
            if (productData.options && productData.options.length > 0) {
              console.log('📝 옵션 데이터 변환 시작');
              // 옵션 데이터를 현재 형식에 맞게 변환
              const convertedOptions = productData.options.map((option: any, index: number) => {
                const converted = {
                  colorName: option.color_name || '',
                  colorCode: option.color_hex || '#000000',
                  sizes: [{ sizeName: option.size || '', quantity: option.quantity || 1 }]
                };
                console.log(`옵션 ${index + 1} 변환:`, option, '→', converted);
                return converted;
              });
              console.log('변환된 옵션 데이터:', convertedOptions);
              setOptions(convertedOptions);
            } else {
              console.log('📝 기본 옵션 설정 (옵션 없음)');
              // 옵션이 없는 경우 기본 옵션 설정
              setOptions([
                { colorName: '', colorCode: '#000000', sizes: [{ sizeName: '', quantity: 1 }] }
              ]);
            }
            
            // sessionStorage에서 데이터 삭제
            console.log('🗑️ sessionStorage에서 데이터 삭제');
            sessionStorage.removeItem('editProductData');
            console.log('✅ 수정 데이터 로드 완료');
            
            // 데이터 로드 완료 표시
            dataLoadedRef.current = true;
            
          } catch (error) {
            console.error('❌ 상품 데이터 파싱 실패:', error);
            console.error('파싱 실패한 데이터:', editData);
            alert('상품 데이터를 불러오는데 실패했습니다. 다시 시도해주세요.');
            navigate('/seller/products');
          } finally {
            setIsLoadingEditData(false);
            console.log('📊 로딩 상태 해제');
          }
        } else {
          // 수정 모드인데 데이터가 없는 경우
          console.warn('⚠️ 수정 모드이지만 상품 데이터가 없습니다.');
          console.log('sessionStorage 키 확인:', Object.keys(sessionStorage));
          alert('상품 데이터를 찾을 수 없습니다. 다시 시도해주세요.');
          navigate('/seller/products');
          setIsLoadingEditData(false);
        }
      } else {
        console.log('📝 일반 등록 모드');
        setIsLoadingEditData(false);
      }
    };

    loadEditData();
  }, [user, isEditMode]); // user 의존성 추가

  // 이미지 업로드
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // 파일 형식 검증
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      alert('PNG, JPG, JPEG 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    // 이미지 압축
    console.log(`🔄 대표 이미지 ${files.length}개 압축 시작...`);
    const compressedFiles = await Promise.all(
      files.map(file => compressProductMainImage(file))
    );
    console.log(`✅ 대표 이미지 ${compressedFiles.length}개 압축 완료`);

    setImages(prev => [...prev, ...compressedFiles].slice(0, 5));
  };
  const handleRemoveImage = (idx: number) => {
    setImages(list => list.filter((_, i) => i !== idx));
  };
  // 상세 이미지 업로드
  const handleDetailImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // 파일 형식 검증
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      alert('PNG, JPG, JPEG 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    try {
      // 각 이미지의 크기 체크
      for (const file of files) {
        const { width, height } = await checkImageSize(file);
        console.log(`이미지 크기: ${file.name} - ${width}x${height}`);

        // 높이 17000픽셀 초과 체크
        if (height > 17000) {
          alert(`이미지 높이가 너무 큽니다.\n파일: ${file.name}\n현재: ${width}x${height}\n최대 지원: 높이 17000픽셀\n\n더 작은 이미지를 사용해주세요.`);
          e.target.value = ''; // 파일 선택 초기화
          return;
        }
      }

      // 이미지 압축
      console.log(`🔄 상세 이미지 ${files.length}개 압축 시작...`);
      const compressedFiles = await Promise.all(
        files.map(file => compressProductDetailImage(file))
      );
      console.log(`✅ 상세 이미지 ${compressedFiles.length}개 압축 완료`);

      setDetailImages(prev => {
        const remain = 10 - prev.length;
        return [...prev, ...compressedFiles.slice(0, remain)];
      });

      console.log(`✅ 상세 이미지 업로드: ${compressedFiles.length}개 파일 (크기 검증 및 압축 완료)`);
    } catch (error) {
      console.error('이미지 처리 중 오류:', error);
      alert('이미지 파일을 처리하는 중 오류가 발생했습니다.');
      e.target.value = '';
    }
  };
  const handleRemoveDetailImage = (idx: number) => {
    setDetailImages(list => list.filter((_, i) => i !== idx));
  };

  // 업로드
  const handleUpload = async () => {
    console.log('=== 업로드 시작 ===');
    console.log('현재 모드:', isEditMode ? '수정' : '등록');
    console.log('폼 데이터:', {
      name,
      priceValue,
      description,
      keywords,
      imagesCount: images.length,
      detailImagesCount: detailImages.length,
      optionsCount: options.length
    });
    
    // 이미 업로드 중이면 중복 실행 방지
    if (isUploading) {
      console.log('⚠️ 이미 업로드 중입니다.');
      return;
    }

    // 4. 필수 입력 항목 검증
    const missingFields = [];
    if (!name.trim()) missingFields.push('상품명');
    if (!priceValue.trim() || Number(priceValue) <= 0) missingFields.push('가격 (0보다 커야 함)');
    if (keywords.length === 0) missingFields.push('키워드');
    if (!description.trim()) missingFields.push('상품 설명');
    // 수정 모드: 기존 이미지 + 새 이미지, 등록 모드: 새 이미지만 확인
    if (images.length === 0 && existingMainImageUrls.length === 0) missingFields.push('대표 이미지 (최소 1장)');
    
    // 옵션 검증
    let hasValidOptions = false;
    if (options.length > 0) {
      for (const option of options) {
        if (option.colorName.trim() && option.sizes.length > 0) {
          for (const size of option.sizes) {
            if (size.sizeName.trim() && Number(size.quantity) > 0) {
              hasValidOptions = true;
              break; // 유효한 사이즈를 찾았으면 더 검사할 필요 없음
            }
          }
        }
        if(hasValidOptions) break; // 유효한 컬러 그룹을 찾았으면 더 검사할 필요 없음
      }
    }
    if (!hasValidOptions) missingFields.push('상품 옵션 (컬러명, 사이즈명, 수량 1 이상)');

    if (missingFields.length > 0) {
      alert(`다음 필수 항목을 입력해주세요:\n- ${missingFields.join('\n- ')}`);
      return;
    }

    // 로딩 상태 시작
    setIsUploading(true);

    // 1. useAuth에서 사용자 정보 가져오기
    if (!user) {
      alert('상품을 등록하려면 로그인이 필요합니다.');
      setIsUploading(false);
      return;
    }

    console.log('🔍 현재 사용자 정보:', {
      id: user.id,
      email: user.email,
      role: user.role
    });

    // seller_profile 정보 확인
    const { data: sellerProfile, error: profileError } = await supabase
      .from('seller_profile')
      .select('id, store_name')
      .eq('user_id', user.id)
      .single();

    console.log('🔍 seller_profile 정보:', { data: sellerProfile, error: profileError });

    if (profileError || !sellerProfile) {
      console.error('❌ seller_profile 조회 실패:', profileError);
      alert('판매자 정보를 찾을 수 없습니다. 판매자 등록을 먼저 완료해주세요.');
      setIsUploading(false);
      return;
    }

    let newProductId: string | null = null;

    try {
      if (isEditMode && editingProductId) {
        console.log('🔧 수정 모드 업로드 시작');
        console.log('수정할 상품 ID:', editingProductId);
        
        // 수정 모드: 기존 상품 업데이트
        
        // 기존 옵션 삭제
        console.log('🗑️ 기존 옵션 삭제 시작');
        const { error: deleteOptionsError } = await supabase
          .from('product_options')
          .delete()
          .eq('product_id', editingProductId);

        if (deleteOptionsError) {
          console.error('❌ 기존 옵션 삭제 실패:', deleteOptionsError);
        } else {
          console.log('✅ 기존 옵션 삭제 완료');
        }

        // 상품 기본 정보 업데이트
        console.log('📝 상품 기본 정보 업데이트 시작');
        const { data: productData, error: productError } = await supabase
          .from('products')
          .update({
            product_name: name,
            price: Number(priceValue),
            description,
            keywords,
            status: 'active',
            is_d2c: isD2c,
            external_url: isD2c ? externalUrl : null,
          })
          .eq('product_id', editingProductId)
          .select('product_id')
          .single();

        if (productError || !productData) {
          console.error('❌ 상품 정보 업데이트 실패:', productError);
          alert('상품 수정에 실패했습니다.');
          return;
        }
        
        console.log('✅ 상품 기본 정보 업데이트 완료:', productData);
        newProductId = productData.product_id;

        // 이미지 URL 업데이트 (기존 URL + 새 이미지 병합)
        console.log('📸 이미지 처리 시작');
        console.log('새 대표 이미지 개수:', images.length);
        console.log('기존 대표 이미지 URL 개수:', existingMainImageUrls.length);
        console.log('원본 대표 이미지 URL 개수:', originalMainImageUrls.length);
        console.log('새 상세 이미지 개수:', detailImages.length);
        console.log('기존 상세 이미지 URL 개수:', existingDetailImageUrls.length);
        console.log('원본 상세 이미지 URL 개수:', originalDetailImageUrls.length);

        // 새로 업로드된 이미지를 R2에 업로드
        // 인덱스는 원본이 아닌 현재 existing 배열 길이를 기준으로 함
        const newMainImageUrls = images.length > 0
          ? await Promise.all(images.map((file, index) =>
              uploadImage(file, String(newProductId), existingMainImageUrls.length + index, 'main')
            ))
          : [];
        const newDetailImageUrls = detailImages.length > 0
          ? await Promise.all(detailImages.map((file, index) =>
              uploadImage(file, String(newProductId), existingDetailImageUrls.length + index, 'detail')
            ))
          : [];

        // 기존 URL + 새 URL 병합
        const finalMainImageUrls = [...existingMainImageUrls, ...newMainImageUrls.filter(Boolean)];
        const finalDetailImageUrls = [...existingDetailImageUrls, ...newDetailImageUrls.filter(Boolean)];

        console.log('최종 대표 이미지 URL:', finalMainImageUrls);
        console.log('최종 상세 이미지 URL:', finalDetailImageUrls);

        // 삭제된 이미지 찾기 (원본에는 있었지만 최종 URL에는 없는 것)
        const deletedMainUrls = originalMainImageUrls.filter(url => !finalMainImageUrls.includes(url));
        const deletedDetailUrls = originalDetailImageUrls.filter(url => !finalDetailImageUrls.includes(url));

        console.log('삭제될 대표 이미지:', deletedMainUrls);
        console.log('삭제될 상세 이미지:', deletedDetailUrls);

        // R2에서 삭제된 이미지 제거
        if (deletedMainUrls.length > 0 || deletedDetailUrls.length > 0) {
          const allDeletedUrls = [...deletedMainUrls, ...deletedDetailUrls];
          await deleteMultipleFromR2(allDeletedUrls);
        }

        // DB 업데이트
        const { error: updateError } = await supabase
          .from('products')
          .update({
            main_images: finalMainImageUrls,
            detail_images: finalDetailImageUrls,
          })
          .eq('product_id', newProductId);

        if (updateError) {
          console.error('❌ 이미지 URL 업데이트 실패:', updateError);
          throw new Error('상품 이미지 정보 저장에 실패했습니다.');
        }

        console.log('✅ 이미지 URL 업데이트 완료');

        // 새 옵션 정보 추가
        if (options.length > 0 && hasValidOptions) {
          console.log('📝 새 옵션 정보 추가 시작');
          console.log('옵션 데이터:', options);
          
          const optionsToInsert = options.flatMap(group =>
            group.sizes
              .filter((size: any) => size.sizeName.trim() && Number(size.quantity) > 0)
              .map((size: { sizeName: string; quantity: number | string }) => ({
                product_id: newProductId,
                color_hex: group.colorCode,
                color_name: group.colorName,
                size: size.sizeName,
                quantity: Number(size.quantity),
              }))
          );

          console.log('삽입할 옵션 데이터:', optionsToInsert);

          if(optionsToInsert.length > 0) {
            const { error: optionsError } = await supabase
              .from('product_options')
              .insert(optionsToInsert);

            if (optionsError) {
              console.error('❌ 옵션 정보 저장 실패:', optionsError);
              throw new Error('상품 옵션 정보 저장에 실패했습니다.');
            }
            
            console.log('✅ 옵션 정보 저장 완료');
          } else {
            console.log('📝 삽입할 옵션이 없음');
          }
        } else {
          console.log('📝 옵션 없음 또는 유효하지 않음');
        }

        console.log('✅ 상품 수정 완료');
        alert('상품이 성공적으로 수정되었습니다!');
        navigate('/seller/products');

      } else {
        console.log('🆕 새 상품 등록 모드 시작');
        
        // 등록 모드: 새 상품 저장
        console.log('🔍 상품 등록 데이터:', {
          seller_id: sellerProfile.id, // user.id가 아닌 sellerProfile.id 사용
          product_name: name,
          price: Number(priceValue),
          description,
          keywords,
          status: 'active',
          store_name: sellerProfile.store_name || 'My Store',
        });
        
        const { data: productData, error: productError } = await supabase
          .from('products')
          .insert({
            seller_id: sellerProfile.id,
            product_name: name,
            price: Number(priceValue),
            description,
            keywords,
            status: 'active',
            store_name: sellerProfile.store_name || 'My Store',
            is_d2c: isD2c,
            external_url: isD2c ? externalUrl : null,
          })
          .select('product_id')
          .single();

        if (productError || !productData) {
          console.error('❌ 상품 정보 저장 실패:', productError);
          alert('상품 등록에 실패했습니다.');
          return;
        }
        
        console.log('✅ 새 상품 기본 정보 저장 완료:', productData);
        newProductId = productData.product_id;

        // 이미지들을 스토리지에 업로드하고 URL 얻기 (병렬 처리)
        const [mainImageUrls, detailImageUrls] = await Promise.all([
          Promise.all(images.map((file, index) => uploadImage(file, String(newProductId), index, 'main'))),
          Promise.all(detailImages.map((file, index) => uploadImage(file, String(newProductId), index, 'detail')))
        ]);

        // 이미지 업로드 실패 체크
        const failedMainImages = mainImageUrls.filter(url => !url);
        const failedDetailImages = detailImageUrls.filter(url => !url);
        
        if (failedMainImages.length > 0 || failedDetailImages.length > 0) {
          throw new Error('이미지 업로드에 실패했습니다.');
        }

        // 얻어온 이미지 URL들을 'products' 테이블에 업데이트
        const { error: updateError } = await supabase
          .from('products')
          .update({
            main_images: mainImageUrls.filter(Boolean),
            detail_images: detailImageUrls.filter(Boolean),
          })
          .eq('product_id', newProductId);

        if (updateError) {
          console.error('이미지 URL 업데이트 실패:', updateError);
          throw new Error('상품 이미지 정보 저장에 실패했습니다.');
        }

        // 옵션 정보들을 'product_options' 테이블에 저장
        if (options.length > 0 && hasValidOptions) {
          const optionsToInsert = options.flatMap(group =>
            group.sizes
              .filter((size: any) => size.sizeName.trim() && Number(size.quantity) > 0)
              .map((size: { sizeName: string; quantity: number | string }) => ({
                product_id: newProductId,
                color_hex: group.colorCode,
                color_name: group.colorName,
                size: size.sizeName,
                quantity: Number(size.quantity),
            }))
          );

          if(optionsToInsert.length > 0) {
            const { error: optionsError } = await supabase
              .from('product_options')
              .insert(optionsToInsert);

            if (optionsError) {
              console.error('옵션 정보 저장 실패:', optionsError);
              throw new Error('상품 옵션 정보 저장에 실패했습니다.');
            }
          }
        }

        alert('상품이 성공적으로 등록되었습니다!');
        navigate('/seller/products');
      }

    } catch (error: any) {
      // 실패 시 생성된 상품 데이터 삭제
      if (newProductId) {
        try {
          await supabase
            .from('products')
            .delete()
            .eq('product_id', newProductId);
        } catch (deleteError) {
          console.error('실패한 상품 데이터 삭제 실패:', deleteError);
        }
      }
      
      alert(`상품 등록에 실패했습니다: ${error.message}`);
      console.error('상품 등록 실패:', error);
    } finally {
      // 로딩 상태 종료 (성공/실패 관계없이)
      setIsUploading(false);
    }
  };

  // 가격 입력 핸들러
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // 숫자만 허용
    const val = e.target.value.replace(/[^0-9]/g, '');
    setPriceValue(val);
  };

  // 옵션창에서 색상/사이즈 선택 핸들러
  const handleOptionSelect = (type: 'color' | 'size', value: string) => {
    setSelectedOption(prev => {
      if (type === 'color') {
        // 색상 바뀌면 사이즈 초기화
        return { color: value, size: '' };
      }
      return { ...prev, [type]: value };
    });
  };

  // 옵션창에 전달할 colors/sizes
  const modalColors = options.map((opt: any) => opt.colorName);
  const modalSizes = options.find((opt: any) => opt.colorName === selectedOption.color)?.sizes.map((s: any) => s.sizeName) || [];

  // 미리보기용 이미지 경로 (기존 URL과 새 File 모두 지원)
  const previewImages = images.length > 0
    ? images.map(f => URL.createObjectURL(f))
    : existingMainImageUrls.length > 0
    ? existingMainImageUrls
    : productImages;
  const previewDetailImages = detailImages.length > 0
    ? detailImages.map(f => URL.createObjectURL(f))
    : existingDetailImageUrls;

  return (
    <Box sx={{ ...styles.pageContainer, p: 3 }}>
      {isLoadingEditData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" color={styles.text.secondary}>
              상품 정보를 불러오는 중...
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
          <Typography variant="h4" fontWeight={700} mb={3} textAlign="center" sx={styles.title}>
            {isEditMode ? '상품 수정' : '상품 등록'}
          </Typography>
        <Stack direction="row" spacing={3} sx={{ flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
          {/* 좌측: 입력 폼 */}
          <Paper sx={{ ...styles.paper, flex: 1, p: 4, minWidth: 400 }}>
            <Stack spacing={3}>
              <TextField fullWidth label="상품명" value={name} onChange={e => setName(e.target.value)} required placeholder="정보를 입력해주세요" sx={styles.input} />
              <TextField 
                fullWidth 
                label="가격" 
                value={priceValue} 
                onChange={handlePriceChange} 
                required 
                placeholder="정보를 입력해주세요"
                onWheel={(e) => e.currentTarget.blur()} // 스크롤 시 포커스 해제
                InputProps={{
                  onWheel: (e) => e.preventDefault(), // 휠 이벤트 차단
                }}
                sx={{
                  ...styles.input,
                  '& input[type=number]': {
                    MozAppearance: 'textfield', // Firefox 스피너 제거
                  },
                  '& input[type=number]::-webkit-outer-spin-button': {
                    WebkitAppearance: 'none', // Chrome/Safari 스피너 제거
                    margin: 0,
                  },
                  '& input[type=number]::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none', // Chrome/Safari 스피너 제거
                    margin: 0,
                  },
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  type: 'text', // 키보드 전용으로 text 타입 사용
                }}
              />
              <KeywordSelector keywords={keywords} setKeywords={setKeywords} />
              <TextField fullWidth label="설명" value={description} onChange={e => setDescription(e.target.value)} multiline minRows={3} placeholder="상품 설명을 입력해주세요" sx={styles.input} />

              {/* D2C 자사몰 연결 (share2dm pro 플랜만 표시) */}
              {isD2cEnabled && (
                <Box sx={{ p: 2, borderRadius: 1, bgcolor: isD2c ? 'rgba(25, 118, 210, 0.08)' : 'transparent', border: isD2c ? '1px solid rgba(25, 118, 210, 0.3)' : `1px solid ${styles.table.cell.borderColor}` }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isD2c}
                        onChange={e => {
                          setIsD2c(e.target.checked);
                          if (!e.target.checked) setExternalUrl('');
                        }}
                        sx={{ color: styles.text.secondary }}
                      />
                    }
                    label={<Typography variant="body2" color={styles.text.primary}>D2C 자사몰 연결 (스티커 탭 시 자사몰로 이동)</Typography>}
                  />
                  {isD2c && (
                    <TextField
                      fullWidth
                      label="자사몰 URL"
                      value={externalUrl}
                      onChange={e => setExternalUrl(e.target.value)}
                      placeholder="https://yourshop.com/product/..."
                      sx={{ ...styles.input, mt: 1 }}
                    />
                  )}
                </Box>
              )}

              <Box>
                <Typography fontWeight={700} mb={1} color={styles.text.primary}>대표 이미지 (최대 5장)</Typography>
                <input type="file" multiple accept="image/png,image/jpg,image/jpeg" hidden ref={fileInputRef} onChange={handleImageChange} />
                <Button variant="outlined" startIcon={<AddPhotoAlternateIcon />} onClick={() => fileInputRef.current?.click()} sx={{ 
                  mb: 2,
                  borderColor: styles.table.cell.borderColor,
                  color: styles.text.primary,
                  '&:hover': {
                    borderColor: styles.text.primary,
                    bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
                  }
                }}>
                  이미지 업로드
                </Button>
                <Stack direction="row" spacing={1}>
                  {/* 기존 URL 이미지 먼저 표시 */}
                  {existingMainImageUrls.map((url, idx) => (
                    <Box
                      key={`existing-${idx}`}
                      sx={{ position: 'relative', borderRadius: 2 }}
                    >
                      <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80, mr: 1 }} />
                      <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }} onClick={() => {
                        setExistingMainImageUrls(list => list.filter((_, i) => i !== idx));
                      }}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                  ))}
                  {/* 새로 업로드된 이미지 표시 */}
                  {images.map((file, idx) => (
                    <Box
                      key={`new-${idx}`}
                      sx={{ position: 'relative', cursor: 'grab', opacity: draggingIdx === idx ? 0.5 : 1, border: dragOverIdx === idx ? '2px solid #1976d2' : 'none', borderRadius: 2 }}
                      draggable
                      onDragStart={e => setDraggingIdx(idx)}
                      onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                      onDrop={e => {
                        e.preventDefault();
                        if (draggingIdx !== null && draggingIdx !== idx) {
                          const newImages = [...images];
                          const [removed] = newImages.splice(draggingIdx, 1);
                          newImages.splice(idx, 0, removed);
                          setImages(newImages);
                        }
                        setDraggingIdx(null);
                        setDragOverIdx(null);
                      }}
                      onDragEnd={() => { setDraggingIdx(null); setDragOverIdx(null); }}
                    >
                      <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80, mr: 1 }} />
                      <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }} onClick={() => handleRemoveImage(idx)}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
              {/* 상세 이미지 업로드 */}
              <Box>
                <Typography fontWeight={700} mb={1} color={styles.text.primary}>상세 이미지 (최대 10장)</Typography>
                <Typography variant="caption" color={styles.text.secondary} sx={{ fontSize: '0.75rem', display: 'block', mb: 1 }}>
                  이미지 높이 최대 17000픽셀
                </Typography>
                <input type="file" multiple accept="image/png,image/jpg,image/jpeg" hidden ref={detailFileInputRef} onChange={handleDetailImageChange} />
                <Button variant="outlined" startIcon={<AddPhotoAlternateIcon />} onClick={() => detailFileInputRef.current?.click()} sx={{
                  mb: 2,
                  borderColor: styles.table.cell.borderColor,
                  color: styles.text.primary,
                  '&:hover': {
                    borderColor: styles.text.primary,
                    bgcolor: isDarkMode ? '#404040' : '#f5f5f5'
                  }
                }}>
                  이미지 업로드
                </Button>
                <Grid container spacing={1} columns={5}>
                  {/* 기존 URL 상세 이미지 먼저 표시 */}
                  {existingDetailImageUrls.map((url, idx) => (
                    <Grid item xs={1} key={`existing-detail-${idx}`}>
                      <Box sx={{ position: 'relative', borderRadius: 2 }}>
                        <Avatar variant="rounded" src={url} sx={{ width: 80, height: 80, mr: 1 }} />
                        <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }} onClick={() => {
                          setExistingDetailImageUrls(list => list.filter((_, i) => i !== idx));
                        }}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                    </Grid>
                  ))}
                  {/* 새로 업로드된 상세 이미지 표시 */}
                  {detailImages.map((file, idx) => (
                    <Grid item xs={1} key={`new-detail-${idx}`}>
                      <Box
                        draggable
                        sx={{
                          position: 'relative',
                          cursor: 'grab',
                          opacity: detailDraggingIdx === idx ? 0.5 : 1,
                          border: detailDragOverIdx === idx ? '2px solid #1976d2' : 'none',
                          borderRadius: 2
                        }}
                        onDragStart={e => setDetailDraggingIdx(idx)}
                        onDragOver={e => { e.preventDefault(); setDetailDragOverIdx(idx); }}
                        onDrop={e => {
                          e.preventDefault();
                          if (detailDraggingIdx !== null && detailDraggingIdx !== idx) {
                            const newDetailImages = [...detailImages];
                            const [removed] = newDetailImages.splice(detailDraggingIdx, 1);
                            newDetailImages.splice(idx, 0, removed);
                            setDetailImages(newDetailImages);
                          }
                          setDetailDraggingIdx(null);
                          setDetailDragOverIdx(null);
                        }}
                        onDragEnd={() => { setDetailDraggingIdx(null); setDetailDragOverIdx(null); }}
                      >
                        <Avatar variant="rounded" src={URL.createObjectURL(file)} sx={{ width: 80, height: 80, mr: 1 }} />
                        <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff' }} onClick={() => handleRemoveDetailImage(idx)}><CloseIcon fontSize="small" /></IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {/* 옵션 입력/리스트 */}
              <OptionEditor options={options} setOptions={setOptions} />
              {/* 1. 등록하기 버튼 디자인(구매하기 버튼과 동일) */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={isUploading}
                  sx={{ 
                    ...styles.button.primary,
                    fontWeight: 700, 
                    fontSize: 18, 
                    px: 4, 
                    py: 1.2,
                    width: '100%',
                    maxWidth: 400,
                    mx: 'auto',
                    display: 'block'
                  }}
                >
                  {isUploading ? '처리 중...' : (isEditMode ? '상품 수정' : '상품 등록')}
                </Button>
              </Box>
            </Stack>
          </Paper>
          {/* 우측: 미리보기 */}
          <Paper sx={{ ...styles.paper, flex: 1, p: 0, minHeight: 800, maxHeight: 800, position: 'relative', maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ maxHeight: 800, overflowY: 'auto', pb: '100px' }}>
              <ProductPreviewModern
                images={previewImages}
                currentImageIdx={currentImageIdx}
                setCurrentImageIdx={setCurrentImageIdx}
                name={name}
                brand={storeName}
                description={description || '(당일발송) two-way leather crop jacket'}
                price={priceValue ? Number(priceValue) : 0}
                reviewScore={4.3}
                reviewCount={18}
                detailImages={previewDetailImages}
              />
            </Box>
            {/* 옵션창: 구매하기 버튼 바로 위에 오버레이 */}
            {optionModalOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 80, // 구매하기 버튼 높이만큼 위
                  zIndex: 400,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <OptionModal
                  open={optionModalOpen}
                  onClose={() => setOptionModalOpen(false)}
                  options={{ colors: modalColors, sizes: modalSizes }}
                  onSelect={handleOptionSelect}
                  selected={selectedOption}
                  price={priceValue ? Number(priceValue) : 0}
                />
              </Box>
            )}
            {/* 구매하기 버튼 (항상 하단 고정 오버레이) */}
            <Box sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 350,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
              pb: 2
            }}>
              <button
                style={{
                  width: '90%', maxWidth: 360, background: '#000', color: '#fff', border: 'none', borderRadius: 20,
                  fontSize: 28, padding: '20px 0', fontFamily: 'inherit', fontWeight: 400, marginBottom: 8, cursor: 'pointer', pointerEvents: 'auto'
                }}
                onClick={() => setOptionModalOpen(true)}
              >
                구매하기
              </button>
            </Box>
          </Paper>
        </Stack>
      </Box>
        )}
    </Box>
  );
} 